/* eslint-disable max-classes-per-file */
import { Asset, Attribute, AttributeType } from '@ess/jg-rule-executor';
import { SettingValue } from '@src/app/model/portal-catalog';
import { PortalModuleCategory } from '@src/app/model/portal-module-category';
import { ToJsonInputTranslatable } from '@src/app/model/to-json-input-translatable';
import { cloneDeep } from 'lodash';
import { StringModuleCategoryFilterSettingGqlInputList } from '@src/app/generated/globalTypes';

export class SingleFilterSetting implements ToJsonInputTranslatable {
  public moduleCategoryId: string;
  public moduleCategoryReference: string;
  public attributeId: string;
  public attributeName: string;
  public attributeType: AttributeType;
  public attributeValue: string;
  public asset: Asset;

  constructor(
    moduleCategoryId?: string,
    moduleCategoryReference?: string,
    attributeId?: string,
    attributeName?: string,
    attributeType?: AttributeType,
    attributeValue?: string,
    asset?: Asset
  ) {
    this.moduleCategoryId = moduleCategoryId;
    this.moduleCategoryReference = moduleCategoryReference;
    this.attributeId = attributeId;
    this.attributeName = attributeName;
    this.attributeType = attributeType;
    this.attributeValue = attributeValue;
    this.asset = asset;
  }

  public static fromJson(json: JSON): SingleFilterSetting {
    const newFs = new SingleFilterSetting();
    newFs.moduleCategoryId = json['moduleCategoryId'];
    newFs.attributeId = json['attributeId'];
    newFs.attributeValue = json['attributeValue'];
    newFs.asset = json['asset'];
    return newFs;
  }

  public toGqlInput(): SingleFilterSetting {
    return new SingleFilterSetting(
      this.moduleCategoryId,
      this.moduleCategoryReference,
      this.attributeId,
      undefined,
      undefined,
      this.attributeValue,
      null
    );
  }
}

export class PortalFilterSettings extends Map<string, Map<string, Map<string, Asset>>> {
  constructor(filterSettings: Map<string, SettingValue>) {
    const tempArray = [];
    filterSettings?.forEach((v1, moduleCategoryReference) => {
      const attributeValueMap = new Map<string, Map<string, Asset>>();
      v1.attributes?.values?.forEach((v2, attributeId) => {
        attributeValueMap.set(attributeId, v2.valueIcons?.values);
      });
      tempArray.push([moduleCategoryReference, attributeValueMap]);
    });
    super(tempArray);
  }

  /**
   * Given the required identifiers, inserts a single asset.
   * Returns the new map, does not update the original!
   * On each level, checks if there exists a map for the given id; if yes it does a set, if not it creates a new map.
   */
  public upsertSimple(
    moduleCategoryReference: string,
    attributeId: string,
    attributeValue: string,
    asset: Asset
  ): PortalFilterSettings {
    const newMap = cloneDeep(this);
    if (newMap.has(moduleCategoryReference)) {
      if (newMap.get(moduleCategoryReference).has(attributeId)) {
        newMap.get(moduleCategoryReference).get(attributeId).set(attributeValue, asset);
      } else {
        newMap.get(moduleCategoryReference).set(attributeId, new Map([[attributeValue, asset]]));
      }
    } else {
      newMap.set(moduleCategoryReference, new Map([[attributeId, new Map([[attributeValue, asset]])]]));
    }
    return newMap;
  }

  public toGqlInput(): StringModuleCategoryFilterSettingGqlInputList {
    const settingValueInput = [];
    this.forEach((v, k) => {
      settingValueInput.push(SettingValue.toGqlInputFromMap(k, v));
    });
    return {
      values: settingValueInput,
    };
  }

  public asObjectArray(categories?: PortalModuleCategory[], attributes?: Attribute[]): SingleFilterSetting[] {
    const objArray: SingleFilterSetting[] = [];
    this.forEach((v1, moduleCategoryRef) => {
      const catId = categories.find(cat => cat.reference === moduleCategoryRef)?.id.toString();
      v1.forEach((v2, attributeId) => {
        let attrName = '';
        let attrType;
        if (attributes) {
          const attr = attributes.find(att => att.id.toString() === attributeId);
          attrName = attr?.name;
          attrType = attr?.attributeType;
        }
        v2.forEach((asset, attributeValue) => {
          objArray.push(new SingleFilterSetting(
            catId,
            moduleCategoryRef,
            attributeId,
            attrName,
            attrType,
            attributeValue,
            asset
          ));
        });
      });
    });
    return objArray;
  }
}

/* eslint-enable max-classes-per-file */
